//dependencies
import React, { memo, ReactElement, useCallback } from "react";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

//components
import { BottomSheet, BottomSheetChildrenProps, useEffectOnce } from "@swiggy-private/core-ui";

// test-ids
import { SWIGGY_MONEY_INFO_TEST_IDS } from "@swiggy-private/payment-test-ids";

//interface
import { SwiggyMoneyInfoHalfCardModel } from "@payment-ui/interfaces";

import styles from "./styles.v4.scss";

interface SwiggyMoneyInfoHalfCardProps extends Omit<SwiggyMoneyInfoHalfCardModel, "key"> {
    onClose: VoidFunction;
    onClickSwiggyMoneyInfoHalfCardActionButton?: VoidFunction;
    onClickSwiggyMoneyInfoHalfCardCloseButton?: VoidFunction;
    onImpressionSwiggyMoneyInfoHalfCard?: VoidFunction;
}

export const ACCESSIBILITY_TEXTS = {
    CLOSE: "Close",
};

function SwiggyMoneyInfoHalfCard({
    onClose,
    heading,
    message,
    ctaMessage,
    onClickSwiggyMoneyInfoHalfCardActionButton,
    onClickSwiggyMoneyInfoHalfCardCloseButton,
    onImpressionSwiggyMoneyInfoHalfCard,
}: SwiggyMoneyInfoHalfCardProps): ReactElement {
    useEffectOnce(() => {
        if (typeof onImpressionSwiggyMoneyInfoHalfCard === "function") {
            onImpressionSwiggyMoneyInfoHalfCard();
        }
    });

    const onClickActionButton = useCallback(
        (closeHalfCard: () => void) => {
            return (): void => {
                if (typeof onClickSwiggyMoneyInfoHalfCardActionButton === "function") {
                    onClickSwiggyMoneyInfoHalfCardActionButton();
                }
                closeHalfCard();
            };
        },
        [onClickSwiggyMoneyInfoHalfCardActionButton],
    );

    return (
        <BottomSheet
            onClose={onClose}
            hideOnOverlayClick
            containerDataTestId={SWIGGY_MONEY_INFO_TEST_IDS.HALF_CARD_CONTAINER}
            closeButtonDataTestId={SWIGGY_MONEY_INFO_TEST_IDS.HALF_CARD_CLOSE_BUTTON}
            onClickCloseButton={onClickSwiggyMoneyInfoHalfCardCloseButton}
        >
            {({ close }: BottomSheetChildrenProps): ReactElement => (
                <>
                    <div className={styles.swiggyMoneyInfoHalfCard}>
                        <Text
                            as="h4"
                            variant="V2.heading2.neutral.bold"
                            color="text_High_Emphasis"
                            className={styles.swiggyMoneyInfoHalfCardHeader}
                        >
                            {heading}
                        </Text>
                        <div className={styles.swiggyMoneyInfoHalfCardBodyContainer}>
                            {message.map((messageText, index) => (
                                <Text
                                    as="p"
                                    variant="V2.body3.neutral.medium"
                                    color="text_Med_Emphasis"
                                    key={index}
                                    className={styles.swiggyMoneyInfoHalfCardBody}
                                >
                                    {messageText}
                                </Text>
                            ))}
                        </div>
                    </div>
                    <div className={styles.swiggyMoneyInfoHalfCardActionButtonWrapper}>
                        <SuperButton
                            variant="fullwidth"
                            buttonType="primary"
                            buttonSize="large"
                            onClick={onClickActionButton(close)}
                        >
                            {ctaMessage}
                        </SuperButton>
                    </div>
                </>
            )}
        </BottomSheet>
    );
}

export default memo(SwiggyMoneyInfoHalfCard);
